import React, { useState } from "react";
import {
  Box,
  Button,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { inputStyle, primaryBtnStyle } from "../styles/styling";
import LoadingOverlay from "../components/LoadingOverlay";
import { triggerNotif } from "../services/NotificationService";
import Toast from "../components/Toast";

type NotificationForm = {
  recipientId?: string;
  topic?: string;
  title: string;
  preview: string;
  body: string;
  imageUrl?: string;
  clickAction?: string;
  ttl?: number;
  messageType: string;
  mobileAppDisplay: "push" | "in-app" | "both" | "test";
  priority: "high" | "normal";
};

const UploadNotification: React.FC = () => {
  const [form, setForm] = useState<NotificationForm>({
    recipientId: undefined,
    topic: undefined,
    title: "",
    preview: "",
    body: "",
    messageType: "",
    mobileAppDisplay: "push",
    priority: "high",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState<boolean>(false);

  const [message, setMessage] = useState<string>("");
  const [severity, setSeverity] = useState<
    "success" | "error" | "warning" | "info"
  >("success");
  const [showToast, setShowToast] = useState<boolean>(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!form.title || form.title.length > 255) {
      newErrors.title = "`title` is required and must be under 255 characters.";
    }

    if (!form.preview) {
      newErrors.preview = "`preview` is required.";
    }

    if (!form.body) {
      newErrors.body = "`body` is required.";
    }

    if (!form.messageType) {
      newErrors.messageType = "`messageType` is required.";
    }

    if (!form.mobileAppDisplay) {
      newErrors.mobileAppDisplay = "`mobileAppDisplay` is required.";
    }

    if (!form.priority) {
      newErrors.priority = "`priority` is required.";
    }

    if ((!form.recipientId || form.recipientId.trim() === "") && !form.topic) {
      newErrors.topic =
        "`topic` is required when `recipientId` is not provided.";
    }

    if (form.imageUrl && !/^https?:\/\/.+\..+/.test(form.imageUrl)) {
      newErrors.imageUrl = "`imageUrl` must be a valid URI.";
    }

    if (form.ttl !== undefined && (isNaN(form.ttl) || form.ttl < 0)) {
      newErrors.ttl = "`ttl` must be a non-negative number.";
    }

    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const validationErrors = validate();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setLoading(false);
    } else {
      setErrors({});
      console.log("Submitting:", form);
      await triggerNotif(form)
        .then(() => {
          setMessage("Successfully uploaded notification.");
          setSeverity("success");
          setLoading(false);
          setShowToast(true);
        })
        .catch((e) => {
          setErrors({ apiError: e });
          setMessage("Failed to upload notification.");
          setSeverity("warning");
          setShowToast(true);
          setLoading(false);
        });
    }
  };

  return (
    <Paper elevation={4} sx={{ padding: 2, maxWidth: 830 }}>
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{ p: 4, maxWidth: 800 }}
      >
        <Typography variant="h4" mb={3}>
          Create Notification
        </Typography>

        <LoadingOverlay loading={loading} />
        <Toast
          message={message}
          severity={severity}
          open={showToast}
          onClose={() => setShowToast(false)}
        />

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Recipient ID"
              name="recipientId"
              fullWidth
              value={form.recipientId || ""}
              onChange={handleChange}
              error={!!errors.recipientId}
              helperText={errors.recipientId}
              sx={inputStyle()}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Topic"
              name="topic"
              fullWidth
              value={form.topic || ""}
              onChange={handleChange}
              error={!!errors.topic}
              helperText={errors.topic}
              sx={inputStyle()}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Title"
              name="title"
              fullWidth
              value={form.title}
              onChange={handleChange}
              error={!!errors.title}
              helperText={errors.title}
              sx={inputStyle()}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Preview"
              name="preview"
              fullWidth
              value={form.preview}
              onChange={handleChange}
              error={!!errors.preview}
              helperText={errors.preview}
              sx={inputStyle()}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Body"
              name="body"
              fullWidth
              multiline
              rows={4}
              value={form.body}
              onChange={handleChange}
              error={!!errors.body}
              helperText={errors.body}
              sx={[inputStyle(), { height: "auto" }]}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Image URL"
              name="imageUrl"
              fullWidth
              value={form.imageUrl || ""}
              onChange={handleChange}
              error={!!errors.imageUrl}
              helperText={errors.imageUrl}
              sx={inputStyle()}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Click Action"
              name="clickAction"
              fullWidth
              value={form.clickAction || ""}
              onChange={handleChange}
              sx={inputStyle()}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="TTL (seconds)"
              name="ttl"
              type="number"
              fullWidth
              value={form.ttl ?? ""}
              onChange={handleChange}
              error={!!errors.ttl}
              helperText={errors.ttl}
              sx={inputStyle()}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Message Type"
              name="messageType"
              fullWidth
              value={form.messageType}
              onChange={handleChange}
              error={!!errors.messageType}
              helperText={errors.messageType}
              sx={inputStyle()}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Mobile App Display"
              name="mobileAppDisplay"
              fullWidth
              value={form.mobileAppDisplay}
              onChange={handleChange}
              error={!!errors.mobileAppDisplay}
              helperText={errors.mobileAppDisplay}
              sx={inputStyle()}
              required
            >
              {["push", "in-app", "both", "test"].map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Priority"
              name="priority"
              fullWidth
              value={form.priority}
              onChange={handleChange}
              error={!!errors.priority}
              helperText={errors.priority}
              sx={inputStyle()}
              required
            >
              {["high", "normal"].map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>

        <Grid container justifyContent="flex-end" mt={4}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            sx={[primaryBtnStyle, { alignSelf: "flex-end" }]}
            disabled={loading}
          >
            Submit Notification
          </Button>
        </Grid>
      </Box>
    </Paper>
  );
};

export default UploadNotification;
